import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { reactQueryClient } from '~/api/reactQuery';
import { Description } from '~/atomic/atom/Description';
import { PaperStyle } from '~/atomic/atom/PaperStyle';
import { ReadMore } from '~/atomic/molecula/ReadMore';
import {
  getUserAbonnementCashName,
  useDisableAbonnementAutoRenewal, useEnableAbonnementAutoRenewal,
  useGetAbonnements, useGetUserAbonnement,
} from '~/atomic/page/bookSubscriptions/abonnementPage.data';
import { AbonnementPageCard } from '~/atomic/page/bookSubscriptions/AbonnementPageCard';
import { AbonnementPageCardDummy } from '~/atomic/page/bookSubscriptions/AbonnementPageCardDummy';
import { DisableAbonnementDiscountModal } from '~/atomic/page/bookSubscriptions/DisableAbonnementDiscountModal';
import { PageTitleStyle, TextRegStyle } from '~/atomic/Typography';
import { openLoginModal } from '~/feature/authorization/auth.data';
import { buyAbonnementActions } from '~/feature/book/buyAbonnement/buyAbonnement.slice';
import { BuyAbonnementChoosePayModal } from '~/feature/book/buyAbonnement/BuyAbonnementChoosePayModal';
import { BuyAbonnementFromAbonnementPageResultModal } from '~/feature/book/buyAbonnement/BuyAbonnementFromAbonnementPageResultModal';
import { isLoggedInSelector } from '~/feature/user/isLoggedInSelector';
import { userSelector } from '~/feature/user/user.selector';
import { AbonnementType } from '~/graphql/books/factory/abonement/AbonnementFactoryTypes';
import { useModal } from '~/lib/hooks/useModal';
import { lessThan } from '~/lib/mediaQuery';
import { useAppDispatch, useAppSelector } from '~/store';

import { ApplyAbonnementDiscountModal } from './ApplyAbonnementDiscountModal';

export const AbonnementPage = () => {
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(isLoggedInSelector);
  const { isAuth } = useAppSelector(userSelector);

  const {
    isOpenApplyAbonnementDiscountModal, closeApplyAbonnementDiscountModal, openApplyAbonnementDiscountModal,
  } = useModal('ApplyAbonnementDiscount');
  const {
    isOpenDisableAbonnementDiscountModal, closeDisableAbonnementDiscountModal, openDisableAbonnementDiscountModal,
  } = useModal('DisableAbonnementDiscount');
  const [selectedAbonnement, changeSelectedAbonnement] = useState<AbonnementType>({} as any);
  const {
    data, isSuccess, isLoading: isLoadingAbonnements, isError,
  } = useGetAbonnements();
  const { data: userAbonnement, isFetching: isFetchingUserAbonnement } = useGetUserAbonnement();
  const disableAbonnementAutoRenewal = useDisableAbonnementAutoRenewal();
  const enableAbonnementAutoRenewal = useEnableAbonnementAutoRenewal();
  const isLoading = isLoadingAbonnements
    || isFetchingUserAbonnement
    || enableAbonnementAutoRenewal.isLoading;

  const changeSelectedAbonnementHandler = async (a) => {
    changeSelectedAbonnement(a);
    if (isLoggedIn) {
      dispatch(buyAbonnementActions.setIsOpenChoosePayModal(true));
    } else {
      dispatch(openLoginModal());
    }
  };

  useEffect(() => {
    if (isAuth) {
      reactQueryClient.invalidateQueries({ queryKey: getUserAbonnementCashName });
    }
  }, [isAuth]);

  const disableAbonnementAutoRenewalHandler = async () => {
    if (userAbonnement.discount.isActive) {
      openDisableAbonnementDiscountModal();
    } else if (userAbonnement.discount.canBeApplied) {
      openApplyAbonnementDiscountModal();
    } else {
      await disableAbonnementAutoRenewal.mutateAsync();
    }
  };

  const enableAbonnementAutoRenewalHandler = async () => {
    await enableAbonnementAutoRenewal.mutateAsync();
  };

  return (
    <>
      <SCBookSubscriptionsPage>
        <SCTitle>
          Абонемент - отличное решение для тех, кто много читает...
        </SCTitle>
        <SCDescriptionWrapper>
          <SCDescriptionWrapperTitle>
            ...потому, что:
          </SCDescriptionWrapperTitle>
          <ReadMore
            linkOpen="Развернуть"
            linkClose="Свернуть"
            isOnlyMobile
            maxHeight={220}
            linkStyle={{
              color: 'var(--black-color)',
              textDecoration: 'underline',
            }}
            text={(
              <Description
                data={[
                  'Открывайте доступ к целому каталогу книг вместо того, чтобы покупать каждую отдельно.',
                  'Стоимость чтения книг в абонементе ниже, чем при разовой покупке.',
                  'Читайте книги, если только они вам интересны',
                  'Можно читать произведения "в процессе", без оплаты их полной стоимости, как завершенной книги!',
                  (
                    <>
                      На книги вне библиотеки абонемента действует
                      {' '}
                      <b>скидка 30%</b>
                      .
                    </>
                  ),
                ]}
              />
            )}
          />
        </SCDescriptionWrapper>
        <SCWrapper>
          {isLoading && new Array(1).fill(1).map((_, i) => <AbonnementPageCardDummy key={i} />)}
          {isError && (
            <SCErrorMessage>
              Не удалось загрузить информацию об абонементах. Попробуйте обновить страницу.
            </SCErrorMessage>
          )}
          {!isLoading && isSuccess ? data.abonnementsTypes.map((abonnement) => (
            <AbonnementPageCard
              key={abonnement.type}
              abonnement={abonnement}
              userAbonnement={userAbonnement}
              connectAbonnement={changeSelectedAbonnementHandler}
              enableAbonnementAutoRenewal={enableAbonnementAutoRenewalHandler}
              disableAbonnementAutoRenewal={disableAbonnementAutoRenewalHandler}
            />
          )) : null}
        </SCWrapper>
      </SCBookSubscriptionsPage>
      <BuyAbonnementChoosePayModal
        price={selectedAbonnement?.price}
        abonnementType={selectedAbonnement?.type}
      />
      <BuyAbonnementFromAbonnementPageResultModal />
      <ApplyAbonnementDiscountModal
        price={userAbonnement?.discount?.price ?? 199}
        isOpen={isOpenApplyAbonnementDiscountModal}
        onClose={closeApplyAbonnementDiscountModal}
      />
      <DisableAbonnementDiscountModal
        price={userAbonnement?.discount?.price ?? 199}
        isOpen={isOpenDisableAbonnementDiscountModal}
        onClose={closeDisableAbonnementDiscountModal}
      />
    </>
  );
};

const SCBookSubscriptionsPage = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 20px;
  padding-top: 32px;
  
  ${lessThan('container-width')} {
    padding-left: 16px;
    padding-right: 16px;
  }
  
  ${lessThan('xs')} {
    padding-top: 20px;
    padding-left: 12px;
    padding-right: 12px;
  }
`;

const SCTitle = styled.h1`
  ${PageTitleStyle};
`;

const SCWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  margin-bottom: 40px;
  width: 100%;

  ${lessThan('xs')} {
    gap: 24px;
  }
`;

const SCDescriptionWrapper = styled.div`
  ${PaperStyle};
  width: 100%;
  padding: 16px 20px;
`;

const SCErrorMessage = styled.div`
  ${PaperStyle};
  width: 100%;
  padding: 20px;
  text-align: center;
  color: var(--error-color, #ff4d4f);
`;

const SCDescriptionWrapperTitle = styled.span`
  display: inline-flex;
  ${TextRegStyle};
  padding-bottom: 10px;
`;
